// Settings › Devices, in words: when each iPad last synced, what it hasn't sent and since when, and
// where its sign-out and wipe stand. The server decides "stale" (the 12-hour alert's rule) and "out
// of date"; these only say them.

import { dateText, dateTimeText, timeText } from '@flowology/kernel';

export interface DeviceRow {
  id: string;
  name: string;
  platform: 'ipados' | 'web' | 'node';
  staffId: string | null;
  personName: string | null;
  appVersion: string;
  outOfDate: boolean;
  pairedAt: string | null;
  lastSeenAt: string | null;
  lastSyncAt: string | null;
  pendingCount: number;
  oldestPendingAt: string | null;
  stale: boolean;
  quarantined: number;
  revokedAt: string | null;
  revokedByName: string | null;
  revokeReason: string | null;
  wipedAt: string | null;
  /** When the person signed out on the iPad itself; null while signed in (or signed out by the office). */
  signedOutAt: string | null;
  /** What they left sealed on it at sign-out (QA-177), or null. */
  sealed: { count: number; since: string | null } | null;
}

/** "iPad", or the phone or computer browser the web version runs in. */
export function platformWord(p: DeviceRow['platform']): string {
  return p === 'ipados' ? 'iPad' : p === 'web' ? 'Web version (a phone or browser)' : 'Test device';
}

/** "12 min", "3 h", "2 days": how long ago, roughly. */
export function agoWords(ms: number): string {
  const m = Math.max(0, Math.floor(ms / 60_000));
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h`;
  const d = Math.floor(h / 24);
  return d === 1 ? '1 day' : `${d} days`;
}

const ago = (at: string, now: Date) => {
  const w = agoWords(now.getTime() - Date.parse(at));
  return w === 'just now' ? w : `${w} ago`;
};

/** "Synced 3 h ago · Oct 16, 9:12 a.m.", or "Never synced". */
export function syncedWords(d: DeviceRow, now: Date, timeZone: string): string {
  return d.lastSyncAt
    ? `Synced ${ago(d.lastSyncAt, now)} · ${dateTimeText(d.lastSyncAt, timeZone, { year: false })}`
    : 'Never synced';
}

/**
 * What it hasn't sent: "Nothing waiting to send", "3 changes waiting for 2 days", or, from an app
 * that doesn't say since when, "3 changes waiting, as of its last sync at 9:12 a.m.". Twelve hours
 * unsynced is said by its chip ("Not synced for 12 hours").
 */
export function unsentWords(d: DeviceRow, now: Date, timeZone: string): string {
  if (!d.pendingCount) return 'Nothing waiting to send';
  const n = d.pendingCount === 1 ? '1 change waiting' : `${d.pendingCount} changes waiting`;
  return d.oldestPendingAt
    ? `${n} for ${agoWords(now.getTime() - Date.parse(d.oldestPendingAt))}`
    : d.lastSyncAt
      ? `${n}, as of its last sync at ${timeText(d.lastSyncAt, timeZone)}`
      : n;
}

/**
 * Its sign-out: "Signed in", "Signed out on the iPad Oct 16" (the person did), or "Signed out Oct 16
 * by Dana Price: Lost" with "Wiped Oct 17" or "Not wiped yet: hasn't connected since Oct 16".
 */
export function statusWords(d: DeviceRow, timeZone: string): { head: string; wipe: string | null } {
  const day = (at: string) => dateText(at, timeZone, { year: false });
  if (!d.revokedAt)
    return { head: d.signedOutAt ? `Signed out on the iPad ${day(d.signedOutAt)}` : 'Signed in', wipe: null };
  const head = `Signed out ${day(d.revokedAt)}${d.revokedByName ? ` by ${d.revokedByName}` : ''}${d.revokeReason ? `: ${d.revokeReason}` : ''}`;
  const wipe = d.wipedAt
    ? `Wiped ${day(d.wipedAt)}`
    : `Not wiped yet: it wipes itself the next time it connects${d.lastSeenAt ? ` (last seen ${day(d.lastSeenAt)})` : ''}`;
  return { head, wipe };
}

/** "2 sent after it was signed out, to review" (Review's quarantine), or null. */
export function quarantinedWords(n: number): string | null {
  if (!n) return null;
  return n === 1
    ? '1 thing sent after it was signed out, to review'
    : `${n} things sent after it was signed out, to review`;
}

/**
 * What a person left sealed on an iPad when they signed out (QA-177, BL-073): "3 changes kept here
 * for Ana Cruz, unsent since Oct 15, 4:10 p.m. They open only if Ana Cruz signs in on this iPad
 * again." Null when it holds none.
 */
export function sealedWords(d: DeviceRow, timeZone: string): { head: string; open: string } | null {
  if (!d.sealed?.count) return null;
  const who = d.personName ?? 'someone no longer here';
  const n = d.sealed.count === 1 ? '1 change' : `${d.sealed.count} changes`;
  const since = d.sealed.since
    ? `, unsent since ${dateTimeText(d.sealed.since, timeZone, { year: false })}`
    : '';
  return {
    // "p.m." ends the sentence, so it carries the full stop
    head: `${n} kept here for ${who}${since}`.replace(/(?<!\.)$/, '.'),
    open: d.personName
      ? `They open only if ${d.personName} signs in on this iPad again.`
      : 'They open only if that person signs in on this iPad again.',
  };
}
